import { setApiBase } from './api/client'
import { applyConsent, type Consent } from './checkout/attribution'
import { components } from './components'
import { type PromoApi, promoApi } from './promo/store'
import { applyPromoVisibility, watchPromoVisibility } from './promo/visibility'
import { applyPromoWords, watchPromoWords } from './promo/words'
import { type EventName, emit, type Handler, off, on } from './runtime/events'
import {
  isMounted,
  mountedCount,
  mountInto,
  pruneDetached,
  unmountAll,
  unmountFrom,
} from './runtime/mount'
import { COMPONENT_ATTR, readComponentName, readProps } from './runtime/props'
import { type AnyProps, register, registerAll, registeredNames } from './runtime/registry'
import { ensureDocumentStyles } from './runtime/styles'
import { queryClient } from './state/query-client'

declare const __CV_VERSION__: string

/** A CSS selector or a live element. */
export type Target = string | Element

export interface CodeverseApi {
  readonly version: string
  mount(name: string, target: Target, props?: AnyProps): void
  unmount(target: Target): boolean
  unmountAll(): void
  scan(root?: ParentNode): number
  register: typeof register
  components(): string[]
  on: typeof on
  off: typeof off
  queryClient: typeof queryClient
  /**
   * The visitor's promo: its code for Klaviyo, `activate(code)` once they sign
   * up, and `offer(code)` or `apply(code)` from a landing page.
   */
  promo: PromoApi
  /** Point the API client elsewhere — used by the dev harness and staging. */
  setApiBase: typeof setApiBase
  debug(): { version: string; mounted: number; components: string[] }
  /**
   * The visitor's cookie consent, from the page's Termly script on each page
   * and whenever it changes: Advertising granted saves the ad parameters the
   * page was reached with, for the checkout; anything else deletes them. And
   * Continue tells Shopify the answer, so the checkout's pixels follow it.
   */
  setConsent(consent: Consent): void
}

declare global {
  interface Window {
    Codeverse?: CodeverseApi
    /** Set before the script loads to suppress the automatic `[data-cv]` scan. */
    __CODEVERSE_NO_AUTOMOUNT__?: boolean
  }
}

function resolveTarget(target: Target): Element | null {
  if (typeof target !== 'string') return target
  const element = document.querySelector(target)
  if (!element) console.warn(`[codeverse] no element matched "${target}"`)
  return element
}

/**
 * Mount every `[data-cv]` element under `root`.
 *
 * Elements naming a component that isn't registered yet are skipped, not
 * consumed — `registry:changed` re-runs the scan so late registration works.
 */
function scan(root: ParentNode = document): number {
  let mounted = 0

  const candidates: Element[] = []
  if (root instanceof Element && root.hasAttribute(COMPONENT_ATTR)) candidates.push(root)
  candidates.push(...root.querySelectorAll(`[${COMPONENT_ATTR}]`))

  for (const element of candidates) {
    if (isMounted(element)) continue
    const name = readComponentName(element)
    if (!name) continue
    if (mountInto(element, name, readProps(element))) mounted++
  }

  return mounted
}

let scanQueued = false

/** Batch the many mutations a single Webflow interaction produces into one scan. */
function scheduleScan(): void {
  if (scanQueued) return
  scanQueued = true
  queueMicrotask(() => {
    scanQueued = false
    scan()
    // A modal Webflow re-inserts is worded for the visitor's code too, and
    // what it marks for their promo shows or hides.
    applyPromoWords()
    applyPromoVisibility()
  })
}

function observeDom(): void {
  if (typeof MutationObserver === 'undefined') return

  const observer = new MutationObserver((mutations) => {
    let added = false
    let removed = false

    for (const mutation of mutations) {
      if (mutation.addedNodes.length) added = true
      if (mutation.removedNodes.length) removed = true
    }

    // Webflow interactions, sliders and tabs clone and re-insert DOM after the
    // initial scan; without this, widgets in those regions never mount.
    if (added) scheduleScan()
    if (removed) pruneDetached()
  })

  observer.observe(document.documentElement, { childList: true, subtree: true })
}

function createApi(): CodeverseApi {
  return {
    version: __CV_VERSION__,

    mount(name, target, props) {
      const element = resolveTarget(target)
      if (element) mountInto(element, name, props ?? {})
    },

    unmount(target) {
      const element = resolveTarget(target)
      return element ? unmountFrom(element) : false
    },

    unmountAll,
    scan,
    register,
    components: registeredNames,
    on,
    off,
    queryClient,
    promo: promoApi,
    setApiBase,

    debug: () => ({
      version: __CV_VERSION__,
      mounted: mountedCount(),
      components: registeredNames(),
    }),

    setConsent: (consent) => applyConsent(consent),
  }
}

function init(): void {
  ensureDocumentStyles()
  if (!window.__CODEVERSE_NO_AUTOMOUNT__) scan()
  // The sign-up modal's words, for the visitor's code (`promo/words.ts`).
  watchPromoWords()
  // The page's own elements marked for a code, or for the promo applied (`promo/visibility.ts`).
  watchPromoVisibility()
  observeDom()

  emit('ready', { version: __CV_VERSION__ })
  // A DOM event as well as the internal bus, so custom code in Webflow can
  // hook in without needing a reference to the API object.
  document.dispatchEvent(
    new CustomEvent('codeverse:ready', { detail: { version: __CV_VERSION__ } }),
  )
}

/**
 * The bundle can legitimately be evaluated twice on a Webflow page (site-wide
 * footer code plus a page-level embed, for instance). The second copy must not
 * re-init or it would double-observe the DOM.
 */
const api: CodeverseApi = window.Codeverse ?? createApi()

if (!window.Codeverse) {
  window.Codeverse = api

  // Registered before init() so the first scan already resolves them.
  registerAll(components)

  // Re-scan when a component registers after the initial pass.
  on('registry:changed', () => scheduleScan())

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true })
  } else {
    init()
  }
}

export default api
export type { AnyProps, EventName, Handler }
